import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ShieldCheck, ShieldAlert, ChevronDown, RefreshCw, Wand2, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SEVERITY = {
  hoch: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30",
  mittel: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
  niedrig: "bg-muted text-muted-foreground border-border",
};

const TYPE_LABEL = {
  placeholder_text: "Platzhalter",
  irrelevant_image: "Bild unpassend",
  contrast: "Kontrast",
  fake_stats: "Kennzahlen",
  layout: "Layout",
  empty_section: "Leere Sektion",
  generic_copy: "Text generisch",
  other: "Sonstiges",
};

const scoreTone = (s) => (s >= 85 ? "text-emerald-600 dark:text-emerald-400" : s >= 65 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400");

/** Qualitätsprüfung (Vision-QA) Strip für den Builder-Header. */
export const QaReportStrip = ({ qa, projectId, API, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);
  const running = !!qa?.running;

  useEffect(() => {
    if (!running) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true, timeout: 15000 });
        if (!r.data?._qa?.running) {
          clearInterval(pollRef.current); pollRef.current = null;
          onUpdated?.(r.data);
          if (r.data?._qa?.error) toast.error(`Prüfung fehlgeschlagen: ${r.data._qa.error}`);
          else toast.success(`Qualitätsprüfung abgeschlossen – Score ${r.data?._qa?.score ?? "–"}/100`);
        }
      } catch (_) { /* weiter pollen */ }
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [running, API, projectId, onUpdated]);

  const start = async (autoFix) => {
    if (starting || running) return;
    setStarting(true);
    try {
      await axios.post(`${API}/projects/${projectId}/qa-check`, { auto_fix: autoFix, use_vision: true }, { withCredentials: true, timeout: 15000 });
      onUpdated?.((prev) => ({ ...prev, _qa: { ...(prev?._qa || {}), running: true, error: null } }));
      setOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Prüfung konnte nicht gestartet werden");
    } finally { setStarting(false); }
  };

  const issues = qa?.issues || [];
  const fixes = qa?.fixes_applied || [];
  const hasReport = typeof qa?.score === "number";
  const Icon = hasReport && qa.score < 65 ? ShieldAlert : ShieldCheck;

  return (
    <div className="rounded-sm border border-border bg-card" data-testid="qa-report-strip">
      <div role="button" tabIndex={0} onClick={() => setOpen((o) => !o)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }} className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/30 transition-colors cursor-pointer" data-testid="qa-strip-toggle">
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          {running ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary" data-testid="qa-running-label">
              <Loader2 size={12} className="animate-spin" /> KI-Qualitätsprüfung läuft – Screenshot wird analysiert…
            </span>
          ) : hasReport ? (
            <>
              <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${scoreTone(qa.score)}`} data-testid="qa-score">
                <Icon size={12} /> Qualität {qa.score}/100
              </span>
              <span className="text-[11px] text-muted-foreground truncate max-w-[52ch]" data-testid="qa-summary">{qa.summary}</span>
              {issues.length > 0 && <span className="text-[11px] text-muted-foreground">· {issues.length} Hinweise</span>}
              {fixes.length > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"><Wand2 size={10} />{fixes.length} Auto-Fixes</span>}
              {qa?.design_revision?.applied && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] bg-primary/10 text-primary" data-testid="qa-design-revision-badge">Art-Director hat Design nachgebessert</span>}
              {qa?.design_revision?.reverted && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] bg-muted text-muted-foreground" data-testid="qa-design-revision-reverted">Design-Revision verworfen (kein besserer Score)</span>}
            </>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"><Eye size={12} /> Noch keine Qualitätsprüfung</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!running && (
            <button type="button" onClick={(e) => { e.stopPropagation(); start(true); }} disabled={starting} className="px-2 py-0.5 rounded-sm bg-foreground text-background text-[10px] font-semibold hover:opacity-90 inline-flex items-center gap-1 disabled:opacity-50" data-testid="qa-run-btn">
              <RefreshCw size={10} /> {hasReport ? "Erneut prüfen + fixen" : "Jetzt prüfen"}
            </button>
          )}
          <ChevronDown size={14} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </div>

      {open && (
        <div className="border-t border-border px-3 py-2.5 space-y-2 bg-muted/10" data-testid="qa-strip-expanded">
          {qa?.error && <div className="text-xs text-red-600" data-testid="qa-error">Fehler: {qa.error}</div>}
          {fixes.length > 0 && (
            <div className="text-xs">
              <div className="font-semibold text-foreground mb-1">Automatisch behoben</div>
              <ul className="space-y-0.5">
                {fixes.map((f, i) => <li key={i} className="text-[11px] text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1 w-full" data-testid={`qa-fix-${i}`}><Wand2 size={10} className="shrink-0" />{f}</li>)}
              </ul>
            </div>
          )}
          {issues.length > 0 ? (
            <div className="text-xs pt-1.5 border-t border-border/40">
              <div className="font-semibold text-foreground mb-1">Offene Hinweise (manuell im Editor anpassen)</div>
              <ul className="space-y-1.5">
                {issues.map((it, i) => (
                  <li key={i} className="flex gap-2 items-start" data-testid={`qa-issue-${i}`}>
                    <span className={`shrink-0 px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold ${SEVERITY[it.severity] || SEVERITY.niedrig}`}>{TYPE_LABEL[it.type] || it.type}</span>
                    <div className="min-w-0">
                      <div className="text-[11px] text-foreground"><span className="font-semibold">{it.where}:</span> {it.description}</div>
                      {it.fix && <div className="text-[11px] text-muted-foreground">→ {it.fix}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : hasReport && !running ? (
            <div className="text-[11px] text-emerald-700 dark:text-emerald-400">Keine offenen Mängel gefunden.</div>
          ) : null}
          {hasReport && (
            <div className="text-[10px] text-muted-foreground pt-1">
              {qa.vision_used ? "Screenshot-Analyse (Gemini)" : "Nur HTML-Prüfung"} · {qa.rounds} Durchlauf{qa.rounds === 1 ? "" : "e"} · {qa.checked_at ? new Date(qa.checked_at).toLocaleString("de-DE") : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
